/**
 * SVG icons for VO Search component.
 * All icons use fill="currentColor" to inherit color from CSS.
 * These are filled/solid style icons for visibility and consistency.
 */

import React from 'react';

/**
 * Search magnifying glass icon.
 * Used in the search input field.
 * @param {Object} props - Component props
 * @param {number} [props.size=18] - Icon size in pixels
 * @returns {JSX.Element} SVG icon
 */
export const SearchIcon = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/>
  </svg>
);

/**
 * Featured content star icon.
 * Indicates featured/highlighted content.
 * Replaces the fire emoji.
 * @param {Object} props - Component props
 * @param {number} [props.size=14] - Icon size in pixels
 * @returns {JSX.Element} SVG icon
 */
export const FeaturedIcon = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M3.612 15.443c-.386.198-.824-.149-.746-.592l.83-4.73L.173 6.765c-.329-.314-.158-.888.283-.95l4.898-.696L7.538.792c.197-.39.73-.39.927 0l2.184 4.327 4.898.696c.441.062.612.636.282.95l-3.522 3.356.83 4.73c.078.443-.36.79-.746.592L8 13.187l-4.389 2.256z"/>
  </svg>
);

/**
 * Paid/locked content icon.
 * Indicates content requires paid membership.
 * Replaces the diamond emoji.
 * @param {Object} props - Component props
 * @param {number} [props.size=14] - Icon size in pixels
 * @returns {JSX.Element} SVG icon
 */
export const PaidIcon = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2zM5 12a1 1 0 1 1 2 0 1 1 0 0 1-2 0zm4 0a1 1 0 1 1 2 0 1 1 0 0 1-2 0z"/>
  </svg>
);

/**
 * Members-only content icon.
 * Indicates content requires free membership.
 * Replaces the lock emoji.
 * @param {Object} props - Component props
 * @param {number} [props.size=14] - Icon size in pixels
 * @returns {JSX.Element} SVG icon
 */
export const MembersIcon = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z"/>
  </svg>
);

/**
 * Clear/close button icon for input field.
 * @param {Object} props - Component props
 * @param {number} [props.size=16] - Icon size in pixels
 * @returns {JSX.Element} SVG icon
 */
export const ClearIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
    <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
  </svg>
);

/**
 * Return/enter key glyph.
 * Sits to the right of the submit button's label (item #16450), which is where
 * The Diff's reference bar puts it -- not as a separate hint beside the input.
 * @param {Object} props - Component props
 * @param {number} [props.size=14] - Icon size in pixels
 * @returns {JSX.Element} SVG icon
 */
export const EnterIcon = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M13 2a.5.5 0 0 1 .5.5v6a2.5 2.5 0 0 1-2.5 2.5H3.707l2.147 2.146a.5.5 0 0 1-.708.708l-3-3a.5.5 0 0 1 0-.708l3-3a.5.5 0 1 1 .708.708L3.707 10H11a1.5 1.5 0 0 0 1.5-1.5v-6A.5.5 0 0 1 13 2z"/>
  </svg>
);

/**
 * Close button icon for modal.
 * @param {Object} props - Component props
 * @param {number} [props.size=20] - Icon size in pixels
 * @returns {JSX.Element} SVG icon
 */
export const CloseIcon = ({ size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854z"/>
  </svg>
);

/**
 * Chevron left icon for pagination.
 * @param {Object} props - Component props
 * @param {number} [props.size=16] - Icon size in pixels
 * @returns {JSX.Element} SVG icon
 */
export const ChevronLeftIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path fillRule="evenodd" d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"/>
  </svg>
);

/**
 * Chevron right icon for pagination.
 * @param {Object} props - Component props
 * @param {number} [props.size=16] - Icon size in pixels
 * @returns {JSX.Element} SVG icon
 */
export const ChevronRightIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path fillRule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"/>
  </svg>
);
